// src/utils/trainStats.js — shared training stats computation
// Used by HealthPage (train coach) and signals.js (Mylo chat context)

const _isoWeekStart = (iso) => {
  const d = new Date(iso + 'T12:00:00');
  const dow = (d.getDay() + 6) % 7; // 0=Mon
  const mon = new Date(d); mon.setDate(d.getDate() - dow);
  return mon.toISOString().slice(0, 10);
};

const _addDays = (iso, n) => {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export function milesInRange(workouts, startDate, endDate) {
  return workouts
    .filter(w => w.date >= startDate && w.date <= endDate && parseFloat(w.dist) > 0)
    .reduce((sum, w) => sum + parseFloat(w.dist || 0), 0);
}

export function weeklyMileageHistory(workouts, weeks = 8) {
  const today = new Date().toISOString().slice(0, 10);
  const thisMonday = _isoWeekStart(today);
  return Array.from({ length: weeks }, (_, i) => {
    const ws = _addDays(thisMonday, -7 * (weeks - 1 - i));
    const we = _addDays(ws, 6);
    return parseFloat(milesInRange(workouts, ws, we).toFixed(1));
  });
}

export function monthlyMileageHistory(workouts, months = 8) {
  const now = new Date();
  return Array.from({ length: months }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (months - 1 - i), 1);
    const start = d.toISOString().slice(0, 10);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
    return parseFloat(milesInRange(workouts, start, end).toFixed(1));
  });
}

export function yearlyMileageHistory(workouts, years = 3) {
  const now = new Date();
  return Array.from({ length: years }, (_, i) => {
    const yr = now.getFullYear() - (years - 1 - i);
    const start = `${yr}-01-01`;
    const end   = `${yr}-12-31`;
    return parseFloat(milesInRange(workouts, start, end).toFixed(1));
  });
}

export function topWeightForExercise(workout, exerciseName) {
  const ex = workout.exercises?.find(e => e.name === exerciseName);
  if (!ex || !ex.sets?.length) return 0;
  return Math.max(...ex.sets.map(s => s.weight || 0));
}

export function computeTrainStats(workouts, today) {
  const sorted = [...workouts].sort((a, b) => b.date.localeCompare(a.date));
  const todayD = new Date(today + 'T12:00:00');

  // Weekly miles (last 7 days)
  const weekAgo = _addDays(today, -6);
  const weeklyMiles = parseFloat(milesInRange(workouts, weekAgo, today).toFixed(1));
  const weeklyWorkoutCount = workouts.filter(w => w.date >= weekAgo && w.date <= today).length;

  // Monthly miles (calendar month)
  const monthStart = `${today.slice(0,7)}-01`;
  const monthlyMiles = parseFloat(milesInRange(workouts, monthStart, today).toFixed(1));

  // Yearly miles
  const yearStart = `${today.slice(0,4)}-01-01`;
  const yearlyMiles = parseFloat(milesInRange(workouts, yearStart, today).toFixed(1));

  // Current run streak (consecutive days with any workout ending today)
  let currentStreak = 0;
  for (let i = 0; i < 365; i++) {
    const d = _addDays(today, -i);
    if (workouts.some(w => w.date === d && w.type !== 'Rest')) currentStreak++;
    else break;
  }

  // Longest streak ever
  let longestStreak = 0, tempStreak = 0, prevDate = null;
  const byDate = [...new Set(workouts.filter(w => w.type !== 'Rest').map(w => w.date))].sort();
  for (const d of byDate) {
    if (prevDate && _addDays(prevDate, 1) === d) tempStreak++;
    else tempStreak = 1;
    longestStreak = Math.max(longestStreak, tempStreak);
    prevDate = d;
  }

  // Longest single run
  const runsWithDist = workouts.filter(w => parseFloat(w.dist) > 0);
  const longestRunW  = runsWithDist.reduce((best, w) =>
    parseFloat(w.dist) > parseFloat(best?.dist || 0) ? w : best, null);
  const longestRun  = longestRunW ? parseFloat(longestRunW.dist) : 0;
  const longestRunDate = longestRunW?.date || null;

  // Total workouts
  const totalWorkouts = workouts.length;

  // Lifetime miles
  const lifetimeMiles = parseFloat(workouts.reduce((s, w) => s + parseFloat(w.dist || 0), 0).toFixed(1));

  // Favorite type
  const typeCounts = {};
  workouts.forEach(w => { typeCounts[w.type] = (typeCounts[w.type] || 0) + 1; });
  const favoriteType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
  const favoriteTypeCount = typeCounts[favoriteType] || 0;

  // Best month
  const monthlySums = {};
  workouts.filter(w => parseFloat(w.dist) > 0).forEach(w => {
    const key = w.date.slice(0, 7);
    monthlySums[key] = (monthlySums[key] || 0) + parseFloat(w.dist);
  });
  const bestMonthKey = Object.entries(monthlySums).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
  const bestMonthMiles = bestMonthKey ? parseFloat(monthlySums[bestMonthKey].toFixed(1)) : 0;
  const bestMonth = bestMonthKey ? { key: bestMonthKey, miles: bestMonthMiles } : null;

  // First workout date
  const firstWorkout = sorted.length > 0 ? sorted[sorted.length - 1].date : null;

  // Lifting streak (consecutive days with Strength or Gym)
  let liftingStreak = 0;
  for (let i = 0; i < 365; i++) {
    const d = _addDays(today, -i);
    if (workouts.some(w => w.date === d && (w.type === 'Strength' || w.type === 'Gym'))) liftingStreak++;
    else break;
  }
  // Best lifting streak ever
  let bestLiftingStreak = 0, tempLiftStreak = 0, prevLiftDate = null;
  const liftDates = [...new Set(workouts.filter(w => w.type === 'Strength' || w.type === 'Gym').map(w => w.date))].sort();
  for (const d of liftDates) {
    if (prevLiftDate && _addDays(prevLiftDate, 1) === d) tempLiftStreak++;
    else tempLiftStreak = 1;
    bestLiftingStreak = Math.max(bestLiftingStreak, tempLiftStreak);
    prevLiftDate = d;
  }

  return {
    weeklyMiles, weeklyWorkoutCount,
    monthlyMiles, yearlyMiles,
    currentStreak, longestStreak,
    liftingStreak, bestLiftingStreak,
    longestRun, longestRunDate,
    totalWorkouts,
    favoriteType, favoriteTypeCount,
    bestMonth,
    lifetimeMiles,
    firstWorkout,
  };
}
